import { StormBuildUpRowDef } from './stormBuildUpTypes';
import { applySectionTitles } from './stormBuildUpTitles';

// Per-tank configurable crate size (metres) — the "1m x 0.4m x 0.5m" in the two
// "Supply & Fit ... Crates" rows. Stored in config.crateDims keyed `ca_<n>_<suffix>`;
// an unset value falls back to the default below.
export const CELLULAR_CRATE_FIELDS = [
  { suffix: 'crateL', label: 'Crate Length (m)', default: 1 },
  { suffix: 'crateW', label: 'Crate Width (m)', default: 0.4 },
  { suffix: 'crateH', label: 'Crate Height (m)', default: 0.5 },
] as const;

// Same three dimensions for the "- Sub Contract" row, configured independently.
export const CELLULAR_SUB_CRATE_FIELDS = [
  { suffix: 'subCrateL', label: 'Sub Contract Crate Length (m)', default: 1 },
  { suffix: 'subCrateW', label: 'Sub Contract Crate Width (m)', default: 0.4 },
  { suffix: 'subCrateH', label: 'Sub Contract Crate Height (m)', default: 0.5 },
] as const;

export const getCellularCrateLabel = (
  n: number,
  crateDims: Record<string, number> = {},
  fields: readonly { suffix: string; default: number }[] = CELLULAR_CRATE_FIELDS,
): string => {
  const [l, w, h] = fields.map((f) => {
    const v = crateDims[`ca_${n}_${f.suffix}`];
    return v && v > 0 ? v : f.default;
  });
  return `${l}m x ${w}m x ${h}m`;
};

const makeCellularTankRows = (n: number, supplierName?: string, crateDims: Record<string, number> = {}): StormBuildUpRowDef[] => {
  const crateLabel = getCellularCrateLabel(n, crateDims);
  const subCrateLabel = getCellularCrateLabel(n, crateDims, CELLULAR_SUB_CRATE_FIELDS);
  return [
  {
    id: `ca_${n}_hdr`,
    description: supplierName?.trim() ? `${supplierName.trim()} Tank ${n}` : `Tank ${n}`,
    unit: '',
    defaultQuant: null,
    isHeader: true,
  },
  { id: `ca_${n}_excCart`,      description: 'Excavate & Cart Onsite',                              unit: 'm3', defaultQuant: null },
  { id: `ca_${n}_levelComp`,    description: 'Level & Compact Formation',                           unit: 'm2', defaultQuant: null },
  { id: `ca_${n}_earthwork`,    description: 'Earthwork Support',                                   unit: 'm2', defaultQuant: null },
  { id: `ca_${n}_terram`,       description: 'Terram surround',                                     unit: 'm2', defaultQuant: null },
  { id: `ca_${n}_membrane`,     description: '1mm thick polypropelene membrane seal around units',  unit: 'm2', defaultQuant: null },
  { id: `ca_${n}_sandBed`,      description: 'Sand Bed & Surround',                                 unit: 'm3', defaultQuant: null },
  { id: `ca_${n}_supplyFit`,    description: `Supply & Fit ${crateLabel} Crates`,               unit: 'm3', defaultQuant: null },
  { id: `ca_${n}_supplyFitSub`, description: `Supply & Fit ${subCrateLabel} Crates - Sub Contract`, unit: 'm3', defaultQuant: null },
  { id: `ca_${n}_backfillCrush`,description: 'Backfill Crush to formation Hard Landscaping',        unit: 'm3', defaultQuant: null },
  { id: `ca_${n}_backfillSoft`, description: 'Backfill as dug to formation Soft Landscaping',       unit: 'm3', defaultQuant: null },
  { id: `ca_${n}_cart`,         description: 'Cart to be added into Site Clearance',                unit: 'm3', defaultQuant: null },
  ];
};

// Data-row (non-header) id suffixes for one tank — used by StormSewersTakeoff.tsx's
// handleAddCellularTank to seed a freshly-added tank's rows at qty 0 instead of
// leaving them undefined (which NumberInput only *displays* as 0 via `?? 0`).
export const CELLULAR_ROW_SUFFIXES = [
  'excCart', 'levelComp', 'earthwork', 'terram', 'membrane',
  'sandBed', 'supplyFit', 'supplyFitSub', 'backfillCrush', 'backfillSoft', 'cart',
];

export const DEFAULT_CELLULAR_SECTIONS = 1;
export const MAX_CELLULAR_SECTIONS = 15;

export const getCellularAttenuationRows = (sectionCount: number, supplierName?: string, crateDims: Record<string, number> = {}, sectionTitles: Record<string, string> = {}): StormBuildUpRowDef[] =>
  applySectionTitles(Array.from({ length: sectionCount }, (_, i) => makeCellularTankRows(i + 1, supplierName, crateDims)).flat(), sectionTitles);

// Full fixed set — kept for parity with stormBuildUpPonds.ts/stormBuildUpSwales.ts's
// convention; no other module currently needs every tank id at once.
export const CELLULAR_ATTENUATION_ROWS: StormBuildUpRowDef[] = getCellularAttenuationRows(MAX_CELLULAR_SECTIONS);

// Shifts a row-id-keyed map's `ca_<n>_*` keys down by one wherever n >
// deletedN, and drops the deleted section's own keys — mirrors
// shiftPondSectionKeys/shiftSwaleSectionKeys.
export const shiftCellularAttenuationSectionKeys = <T,>(map: Record<string, T>, deletedN: number): Record<string, T> => {
  const next: Record<string, T> = {};
  Object.entries(map).forEach(([id, value]) => {
    const match = id.match(/^ca_(\d+)_(.+)$/);
    if (!match) {
      next[id] = value;
      return;
    }
    const idx = Number(match[1]);
    if (idx === deletedN) return;
    const newIdx = idx > deletedN ? idx - 1 : idx;
    next[`ca_${newIdx}_${match[2]}`] = value;
  });
  return next;
};
